import { styled } from "goober";
import type * as React from "react";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { PressButton } from "./PressButton";
import { Text } from "./Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "./ToggleIcon";

export type FilterOption = {
  id: string;
  label: string;
  menuLeft?: React.ReactNode;
  pillLeft?: React.ReactNode;
};

export type FilterDef =
  | {
      kind: "single";
      key: string;
      label: string;
      icon: React.ReactNode;
      options: FilterOption[];
      value: string | null;
      defaultValue?: string;
      onSelect: (id: string) => void;
      onClear: () => void;
    }
  | {
      kind: "multi";
      key: string;
      label: string;
      icon: React.ReactNode;
      options: FilterOption[];
      values: string[];
      onToggle: (id: string) => void;
    };

const FilterButtonWrapper = styled("div")`
  position: relative;
  display: inline-flex;
`;

const FilterActiveDot = styled("div")`
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--surface-primary);

  &::after {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--red-600);
  }
`;

const FilterBadge = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  background: var(--alpha4);
  border: none;
  border-radius: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  &:hover {
    background: var(--alpha8);
    ${DefaultIcon} {
      opacity: 0;
      transform: rotate(90deg) scale(0.5);
    }
    ${HoverIcon} {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }
`;

export function hasActiveFilters(filters: FilterDef[]) {
  return filters.some((f) =>
    f.kind === "single" ? f.value != null : f.values.length > 0
  );
}

export function ListFilter({ filters }: { filters: FilterDef[] }) {
  const active = hasActiveFilters(filters);
  return (
    <Dropdown
      opener={
        <FilterButtonWrapper>
          <Button variant="secondary" icon="list-filter" />
          {active ? <FilterActiveDot /> : null}
        </FilterButtonWrapper>
      }
      align="bottom"
      minWidth={180}
      sections={[
        {
          items: filters.map((filter) => ({
            type: "submenu" as const,
            left: filter.icon,
            label: filter.label,
            disabled: filter.options.length === 0,
            searchable: filter.options.length >= 10,
            submenu: [
              {
                items: filter.options.map((option) => ({
                  type: "button" as const,
                  left: option.menuLeft ?? null,
                  label: option.label,
                  right: (
                    <Icon
                      name="check"
                      size={12}
                      color={
                        (
                          filter.kind === "single"
                            ? (filter.value ?? filter.defaultValue ?? null) ===
                              option.id
                            : filter.values.includes(option.id)
                        )
                          ? "var(--text-secondary)"
                          : "transparent"
                      }
                    />
                  ),
                  onClick: () => {
                    if (filter.kind === "single") {
                      const selected =
                        (filter.value ?? filter.defaultValue ?? null) ===
                        option.id;
                      if (selected) filter.onClear();
                      else filter.onSelect(option.id);
                    } else filter.onToggle(option.id);
                  },
                })),
              },
            ],
          })),
        },
      ]}
    />
  );
}

const centeredDefaultIconStyle: React.CSSProperties = {
  inset: 0,
  alignItems: "center",
  justifyContent: "center",
};

export function ListFilterPills({ filters }: { filters: FilterDef[] }) {
  if (!hasActiveFilters(filters)) return null;
  return (
    <>
      <Text variant="text-small" color="var(--text-secondary)">
        Filtered by:
      </Text>
      {filters.flatMap((filter) => {
        if (filter.kind === "single") {
          if (filter.value == null) return [];
          const option = filter.options.find((o) => o.id === filter.value);
          if (!option) return [];
          return [
            <FilterBadge
              key={`${filter.key}:${option.id}`}
              onPress={() => filter.onClear()}
              title="Clear filter"
            >
              <ToggleIconContainer>
                <DefaultIcon style={centeredDefaultIconStyle}>
                  {option.pillLeft ?? option.menuLeft}
                </DefaultIcon>
                <HoverIcon>
                  <Icon name="x" size={12} color="var(--text-secondary)" />
                </HoverIcon>
              </ToggleIconContainer>
              <Text variant="text-smaller-md" color="var(--text-secondary)">
                {option.label}
              </Text>
            </FilterBadge>,
          ];
        }
        return filter.values
          .map((id) => {
            const option = filter.options.find((o) => o.id === id);
            if (!option) return null;
            return (
              <FilterBadge
                key={`${filter.key}:${id}`}
                onPress={() => filter.onToggle(id)}
                title="Remove filter"
              >
                <ToggleIconContainer>
                  <DefaultIcon style={centeredDefaultIconStyle}>
                    {option.pillLeft ?? option.menuLeft}
                  </DefaultIcon>
                  <HoverIcon>
                    <Icon name="x" size={12} color="var(--text-secondary)" />
                  </HoverIcon>
                </ToggleIconContainer>
                <Text variant="text-smaller-md" color="var(--text-secondary)">
                  {option.label}
                </Text>
              </FilterBadge>
            );
          })
          .filter((v): v is NonNullable<typeof v> => v != null);
      })}
    </>
  );
}

export function parseMultiFilter(param: string | undefined): string[] {
  return param && param !== "all" ? param.split(",").filter(Boolean) : [];
}

export function toggleMultiFilter(values: string[], id: string): string {
  const next = values.includes(id)
    ? values.filter((v) => v !== id)
    : [...values, id];
  return next.length === 0 ? "all" : next.join(",");
}
